import React, { useEffect, useMemo, useState } from "react";
import { Clock, TrendingUp, Mic, FileText, Stethoscope, Sparkles, Activity, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard, Loading } from "@/components/Primitives";

const MODULE_META = {
  "dictation":      { label: "ARIA Dictation",   icon: Mic },
  "encounter-prep": { label: "Encounter Prep",   icon: FileText },
  "cardiology":     { label: "Cardiology Calcs", icon: Stethoscope },
  "prior-auth":     { label: "Prior Auth",       icon: ShieldCheck },
  "referrals":      { label: "Referrals",        icon: Sparkles },
  "charting":       { label: "Charting",         icon: Activity },
};

function fmtMin(m) {
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60); const r = Math.round(m % 60);
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

export default function TimeSaved() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/time-saved/summary").then((r) => setData(r.data)).catch(() => setData({ total_min: 0, this_week_min: 0, by_module: [], by_week: [] })); }, []);

  const kpis = useMemo(() => {
    if (!data) return null;
    const equivalent_visits = Math.round((data.this_week_min || 0) / 15);
    const equivalent_notes  = Math.round((data.this_week_min || 0) / 10);
    return {
      this_week: fmtMin(data.this_week_min || 0),
      total:     fmtMin(data.total_min || 0),
      visits:    equivalent_visits,
      notes:     equivalent_notes,
    };
  }, [data]);

  if (!data) return <Loading label="Crunching your time-saved metrics…" />;

  const maxModuleMin = Math.max(1, ...data.by_module.map((m) => m.minutes_saved || 0));
  const maxWeekMin   = Math.max(1, ...data.by_week.map((w) => w.minutes_saved || 0));

  return (
    <div data-testid="time-saved-page">
      <PageHeader
        eyebrow="Burnout reduction"
        title="Time saved with ZidaMD"
        subtitle="Hard data on hours returned to your day. Captured from real module usage — not estimates."
        testid="time-saved-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={Clock} label="This week" value={kpis.this_week} testid="kpi-this-week" accent />
        <KpiCard icon={TrendingUp} label="All-time" value={kpis.total} testid="kpi-total" />
        <KpiCard icon={Stethoscope} label="≈ Patient visits" value={kpis.visits} testid="kpi-visits" />
        <KpiCard icon={FileText} label="≈ Notes drafted" value={kpis.notes} testid="kpi-notes" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Time saved by module" subtitle="Last 4 weeks" testid="by-module-card">
          {data.by_module.length === 0 ? (
            <EmptyState label="No usage data yet — start dictating, generating briefs, or running calcs to populate this view." />
          ) : (
            <ul className="space-y-3">
              {data.by_module.map((m) => {
                const meta = MODULE_META[m.module] || { label: m.module, icon: Activity };
                const pct = Math.round(((m.minutes_saved || 0) / maxModuleMin) * 100);
                const Icon = meta.icon;
                return (
                  <li key={m.module} data-testid={`module-row-${m.module}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="inline-flex items-center gap-2 text-brand-text font-medium">
                        <Icon className="w-4 h-4 text-brand-primary" /> {meta.label}
                      </span>
                      <span className="tabular-nums text-brand-muted">
                        <b className="text-brand-text">{fmtMin(m.minutes_saved || 0)}</b>
                        <span className="ml-2 text-[10px] uppercase tracking-wide">{m.event_count} uses</span>
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-brand-bg overflow-hidden">
                      <div className="h-full rounded-full transition-all"
                           style={{ width: `${pct}%`, background: "linear-gradient(90deg,#4c1d95,#c9a24b)" }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Weekly trend" subtitle="Past 8 weeks" testid="weekly-trend-card">
          {data.by_week.length === 0 ? (
            <EmptyState label="Use ZidaMD this week to start tracking your weekly trend." />
          ) : (
            <div className="flex items-end gap-2 h-48 px-2">
              {data.by_week.map((w) => {
                const h = Math.max(4, ((w.minutes_saved || 0) / maxWeekMin) * 160);
                const label = w.week_label || "—";
                return (
                  <div key={w.week_label} className="flex-1 flex flex-col items-center group" data-testid={`week-bar-${w.week_label}`}>
                    <div className="text-[9px] text-brand-muted mb-1 opacity-0 group-hover:opacity-100 transition tabular-nums">
                      {fmtMin(w.minutes_saved || 0)}
                    </div>
                    <div className="w-full rounded-t" style={{ height: `${h}px`, background: "linear-gradient(180deg,#7c3aed,#4c1d95)" }} />
                    <div className="text-[9px] text-brand-muted mt-1 tabular-nums">{label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Why this matters" testid="why-it-matters" className="mt-6">
        <div className="grid sm:grid-cols-3 gap-4 text-sm text-brand-text">
          <Stat headline="Avg US physician loses 6 hrs/week to documentation alone." source="JAMA Internal Medicine, 2024" />
          <Stat headline="44% of physicians report burnout — top driver: clerical burden." source="Medscape National Physician Burnout Report" />
          <Stat headline="Every 10 minutes returned = ~$120 in clinical revenue capacity." source="MGMA Provider Productivity Survey" />
        </div>
      </SectionCard>

      <div className="mt-6 text-[10px] italic text-brand-muted text-center px-4 leading-relaxed">
        Minutes-saved estimates are calibrated against published industry benchmarks and your individual usage volume. Track is per-physician, never shared.
      </div>
    </div>
  );
}

const EmptyState = ({ label }) => (
  <div className="py-10 text-center text-sm text-brand-muted">{label}</div>
);

const Stat = ({ headline, source }) => (
  <div className="rounded-md bg-brand-bg p-3 border-l-4 border-brand-primary">
    <div className="text-sm font-semibold text-brand-text leading-snug">{headline}</div>
    <div className="text-[10px] text-brand-muted mt-2 italic">Source: {source}</div>
  </div>
);
